import { useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import TinymceEditor from "../../../components/TinymceEditor";
import { EMAIL_EDITOR_INIT } from "../../emailTemplates/editorConfig";
import {
  emailInvoice,
  getInvoiceEmail,
} from "../../../distflowAPI/invoicesApi";
import useAppSelector from "../../../hooks/useAppSelector";

// Lets the user check and edit the invoice email before it is sent.
// The subject and message start from the "Single invoice" email template; the
// invoice table and the PDF are added automatically.
export default function EmailInvoiceDialog({ open, invoiceId, onClose }) {
  const isStaff = useAppSelector(
    (state) => state.user.authUser?.is_staff === true
  );
  const [preview, setPreview] = useState(null);
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [showInvoice, setShowInvoice] = useState(false);
  const editorRef = useRef(null);

  useEffect(() => {
    if (!open || !invoiceId) return;
    setPreview(null);
    setError("");
    setShowInvoice(false);
    setLoading(true);
    getInvoiceEmail(invoiceId)
      .then((res) => {
        setPreview(res.data);
        setSubject(res.data.subject);
        setMessage(res.data.message);
      })
      .catch((err) =>
        setError(err?.response?.data?.error || "Could not load the email.")
      )
      .finally(() => setLoading(false));
  }, [open, invoiceId]);

  function handleSend() {
    setSending(true);
    setError("");
    emailInvoice(invoiceId, { subject, message })
      .then(() => {
        alert("Invoice emailed successfully");
        onClose();
      })
      .catch((err) =>
        setError(err?.response?.data?.error || "Could not email the invoice.")
      )
      .finally(() => setSending(false));
  }

  const noRecipients = preview && preview.recipients.length === 0;
  // The invoice as the customer will see it, with the edited message.
  const fullEmail = preview
    ? preview.html.replace(preview.message, message)
    : "";

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Email invoice #{invoiceId}</DialogTitle>
      <DialogContent>
        {loading && <CircularProgress size={24} />}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {preview && (
          <Stack spacing={2} mt={1}>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              flexWrap="wrap"
            >
              <Typography variant="body2">To:</Typography>
              {preview.recipients.map((email) => (
                <Chip key={email} size="small" label={email} />
              ))}
              {noRecipients && (
                <Typography variant="body2" color="error">
                  This account has no email address. Add one to the account
                  first.
                </Typography>
              )}
            </Stack>
            <TextField
              label="Subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              fullWidth
              size="small"
            />
            <Box>
              <Typography variant="body2" mb={0.5}>
                Message (shown above the invoice table)
              </Typography>
              <TinymceEditor
                value={message}
                onEditorChange={setMessage}
                height={260}
                init={EMAIL_EDITOR_INIT}
                onInit={(editor) => (editorRef.current = editor)}
              />
              <Stack
                direction="row"
                spacing={1}
                mt={1}
                flexWrap="wrap"
                useFlexGap
              >
                <Typography variant="caption" color="text.secondary">
                  Insert:
                </Typography>
                {Object.entries(preview.placeholders || {}).map(
                  ([name, description]) => (
                    <Chip
                      key={name}
                      size="small"
                      variant="outlined"
                      label={`{${name}}`}
                      title={description}
                      onClick={() =>
                        editorRef.current?.insertContent(`{${name}}`)
                      }
                    />
                  )
                )}
              </Stack>
            </Box>
            <Box>
              <Button size="small" onClick={() => setShowInvoice(!showInvoice)}>
                {showInvoice ? "Hide" : "Show"} the full email with the invoice
              </Button>
              {showInvoice && (
                <Box
                  component="iframe"
                  title="Invoice email preview"
                  srcDoc={fullEmail}
                  sx={{ width: "100%", height: 420, border: "1px solid #ddd" }}
                />
              )}
            </Box>
            <Typography variant="caption" color="text.secondary">
              The invoice PDF is attached.
              {isStaff && (
                <>
                  {" "}
                  To change the wording for every invoice email, edit the{" "}
                  <Link component={RouterLink} to="/email-templates">
                    email templates
                  </Link>
                  .
                </>
              )}
            </Typography>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          color="warning"
          onClick={handleSend}
          disabled={!preview || sending || noRecipients || !subject.trim()}
        >
          {sending ? "Sending..." : "Send"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
